// Prompt 0470 M8 — Tab Asignaciones del panel TipoRolGobernanzaDef.
// - Si esPorProceso=true: vista read-only con lista proceso → puesto.
// - Si esPorProceso=false: multi-selector de puestos (add/remove -> API).

import type React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  fetchTipoRolGobernanza,
  crearRolGobernanza,
  desactivarRolGobernanza,
  fetchPuestos,
  type TipoRolGobernanzaAdmin,
  type TipoRolGobernanzaDetalle,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { LoadingSpinner } from '../../../../../../shared/components/LoadingSpinner';

interface AsignacionesTabProps {
  tipo: TipoRolGobernanzaAdmin;
  onChanged?: () => void;
}

interface PuestoMin {
  id: string;
  titulo: string;
  codigo?: string | null;
}

export function AsignacionesTab({ tipo, onChanged }: AsignacionesTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const panel = ((adm ?? {}) as Record<string, unknown>).tipoRolGobernanzaPanel as
    | Record<string, string>
    | undefined;
  const lbl = panel ?? {};

  const [detalle, setDetalle] = useState<TipoRolGobernanzaDetalle | null>(null);
  const [puestos, setPuestos] = useState<PuestoMin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPuesto, setSelectedPuesto] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [detRes, puestosRes] = await Promise.all([
        fetchTipoRolGobernanza(tipo.id),
        fetchPuestos({ incluirInactivos: 'false' }),
      ]);
      setDetalle(detRes.data);
      const pRaw = (puestosRes.data ?? []) as Array<{
        id: string;
        titulo: string;
        codigo?: string | null;
      }>;
      setPuestos(pRaw.map((p) => ({ id: p.id, titulo: p.titulo, codigo: p.codigo ?? null })));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [tipo.id]);

  useEffect(() => {
    load();
  }, [load]);

  const asignar = async () => {
    if (!selectedPuesto) return;
    setSaving(true);
    setError('');
    try {
      await crearRolGobernanza({ puestoId: selectedPuesto, tipoRolId: tipo.id });
      setSelectedPuesto('');
      await load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al asignar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const desasignar = async (rolId: string) => {
    if (!window.confirm(lbl.confirmDesasignar ?? 'Quitar esta asignacion?')) return;
    setSaving(true);
    setError('');
    try {
      await desactivarRolGobernanza(rolId);
      await load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al desasignar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="sm" muted />;

  if (!detalle) {
    return (
      <div style={{ padding: 20, color: '#6B7280' }}>
        {lbl.errorCargar ?? 'No se pudo cargar el detalle'}
      </div>
    );
  }

  const puestosAsignadosIds = new Set(detalle.roles.map((r) => r.puestoId));
  const puestosDisponibles = puestos.filter((p) => !puestosAsignadosIds.has(p.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {tipo.esPorProceso ? (
        <div
          style={{
            padding: '10px 14px',
            background: '#F0F9FF',
            color: '#075985',
            borderRadius: 8,
            fontSize: 12,
            border: '1px solid #BAE6FD',
          }}
        >
          {lbl.porProcesoInfo ??
            'Los roles por proceso se gestionan desde la ficha de cada proceso.'}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: '10px 12px',
            background: '#F9FAFB',
            borderRadius: 8,
          }}
        >
          <select
            value={selectedPuesto}
            onChange={(e) => setSelectedPuesto(e.target.value)}
            style={{ flex: 1, ...inputStyle }}
          >
            <option value="">{lbl.seleccionarPuesto ?? 'Selecciona un puesto'}</option>
            {puestosDisponibles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.titulo}
                {p.codigo ? ` (${p.codigo})` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={asignar}
            disabled={!selectedPuesto || saving}
            style={{
              padding: '8px 16px',
              background: !selectedPuesto || saving ? '#9CA3AF' : '#1F3864',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: !selectedPuesto || saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + {lbl.asignar ?? 'Asignar puesto'}
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '10px 14px',
            background: '#FEF2F2',
            color: '#B91C1C',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #FECACA',
          }}
        >
          {error}
        </div>
      )}

      {/* Lista de asignaciones */}
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#374151' }}>
          {lbl.asignacionesActivas ?? 'Asignaciones activas'} ({detalle.roles.length})
        </h4>
        {detalle.roles.length === 0 ? (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: '#6B7280',
              background: '#F9FAFB',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {lbl.sinAsignaciones ?? 'Sin asignaciones activas'}
          </div>
        ) : (
          <div
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {detalle.roles.map((r) => {
              const persona = r.puesto.asignaciones?.[0]?.persona;
              const personaText = persona
                ? [persona.nombre, persona.apellidos].filter(Boolean).join(' ')
                : (lbl.vacante ?? 'Vacante');
              return (
                <div
                  key={r.id}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 13,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0F172A' }}>
                      {r.puesto.titulo}
                      {r.puesto.codigo && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontFamily: 'JetBrains Mono, monospace',
                            color: '#6B7280',
                            fontSize: 11,
                          }}
                        >
                          {r.puesto.codigo}
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>
                      {personaText}
                      {r.proceso && (
                        <>
                          {' · '}
                          <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {r.proceso.codigo}
                          </span>{' '}
                          {r.proceso.nombre}
                        </>
                      )}
                    </div>
                  </div>
                  {!tipo.esPorProceso && (
                    <button
                      onClick={() => desasignar(r.id)}
                      disabled={saving}
                      style={{
                        background: 'none',
                        border: '1px solid #FCA5A5',
                        color: '#DC2626',
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 11,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                      }}
                      title={lbl.quitar ?? 'Quitar asignacion'}
                    >
                      ✕ {lbl.quitar ?? 'Quitar'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};
